import type { Channel } from '@getaccept/pusher';
import { DealroomEvent, EditorEvent } from '@getaccept/pusher';
import { storeToRefs } from 'pinia';
import type { MaybeRef, Ref } from 'vue';
import { unref, onBeforeUnmount, ref, watch } from 'vue';
import { pusherClient } from '../../api/pusher/pusher-client';
import { usePusherStore } from '../../pusher/store/pusher.store';
import { useContextualCommentingPusherData } from './contextual-commenting-pusher-data.composables';

export const useContextualCommentingPusher = (
  showContextualCommenting: Ref<boolean>,
  contentIds: MaybeRef<string[]> = []
) => {
  const pusherStore = usePusherStore();
  const { channels } = storeToRefs(pusherStore);

  const {
    onCommentCreated,
    onCommentUpdated,
    onCommentRead,
    onCommentDeleted,
    onReplyRead,
  } = useContextualCommentingPusherData();

  const lockedContentIds: Ref<string[]> = ref([]);

  const resetLockedContentIds = () => {
    lockedContentIds.value = [];
  };

  const listenersAdded = ref(false);

  const addListeners = () => {
    if (listenersAdded.value) {
      return;
    }
    pusherClient.listen(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentCreated,
      onCommentCreated
    );
    pusherClient.listen(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentUpdated,
      onCommentUpdated
    );
    pusherClient.listen(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentDeleted,
      onCommentDeleted
    );
    pusherClient.listen(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentRead,
      onCommentRead
    );
    pusherClient.listen(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentReplyCreated,
      onCommentUpdated
    );
    pusherClient.listen(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentReplyUpdated,
      onCommentUpdated
    );
    pusherClient.listen(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentReplyRead,
      onReplyRead
    );

    listenersAdded.value = true;
  };

  watch(showContextualCommenting, () => {
    if (!channels.value?.dealroom) {
      return;
    }
    if (!showContextualCommenting.value && listenersAdded.value) {
      removeAllListeners();
      return;
    }
    if (listenersAdded.value) {
      return;
    }
    addListeners();
  });

  const removeAllListeners = () => {
    pusherClient.unbind(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentCreated,
      onCommentCreated
    );
    pusherClient.unbind(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentUpdated,
      onCommentUpdated
    );
    pusherClient.unbind(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentDeleted,
      onCommentDeleted
    );
    pusherClient.unbind(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentRead,
      onCommentRead
    );
    pusherClient.unbind(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentReplyCreated,
      onCommentUpdated
    );
    pusherClient.unbind(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentUpdated,
      onCommentUpdated
    );
    pusherClient.unbind(
      channels.value.dealroom as Channel,
      DealroomEvent.ContextualCommentReplyRead,
      onReplyRead
    );
    listenersAdded.value = false;
  };

  const editorUpdated = (id: string) => {
    if (lockedContentIds.value.includes(id)) {
      return;
    }
    lockedContentIds.value.push(id);
  };

  const addEditorListeners = async () => {
    await pusherStore.subscribeToEditorChannels(unref(contentIds));
    channels.value.editor.forEach((editorChannel: Channel) => {
      pusherClient.listen(editorChannel, EditorEvent.Updated, ({ contentId }) =>
        editorUpdated(contentId)
      );
    });
  };

  const removeEditorListeners = () => {
    channels.value.editor.forEach((editorChannel: Channel) =>
      editorChannel.unbind(EditorEvent.Updated, ({ contentId }) => editorUpdated(contentId))
    );
    pusherStore.unsubscribeFromEditorChannels();
  };

  watch(
    () => unref(contentIds),
    (newIds: string[], oldIds: string[]) => {
      if ((!newIds.length && !oldIds?.length) || !showContextualCommenting.value) {
        return;
      }

      const hasIdsChanged =
        newIds.length !== oldIds.length ||
        !newIds.every(id => oldIds.includes(id)) ||
        !oldIds.every(id => newIds.includes(id));

      if (hasIdsChanged) {
        removeEditorListeners();
        addEditorListeners();
      }
    }
  );

  onBeforeUnmount(() => {
    removeAllListeners();
    removeEditorListeners();
  });

  return { lockedContentIds, addListeners, resetLockedContentIds };
};
